import React, { useEffect, useState } from 'react';
import { FaEdit, FaTrash, FaCopy, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Styles from './grouppolicyStyle.module.scss';
import { useAuth } from 'oidc-react';
import Project from '../../common/Utils/project';
import { LoaderCircle } from 'lucide-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api from '../../components/services/apiService';
import DynamicDataTooltip from '../../components/DynamicDataTooltip/DynamicDataTooltip';
import { usePermissions } from '../../hooks/usePermissions';

// Types
interface TenantGroup {
  tenantGroupId?: string;
  tenantGroupID?: string; // API returns this uppercase-ID variant
  tenantGroupName: string;
}

interface Policy {
  groupPolicyAutomationId: string;
  policyName: string;
  tenantId: string;
  tenantGroups: TenantGroup[];
}

const VISIBLE_GROUP_COUNT = 3;

const renderGroupsCell = (tenantGroups?: TenantGroup[]) => {
  const groupNames = (tenantGroups ?? []).map((group) => group.tenantGroupName).filter(Boolean);

  if (groupNames.length === 0) {
    return <span style={{ color: '#888' }}>—</span>;
  }

  const visibleGroups = groupNames.slice(0, VISIBLE_GROUP_COUNT);
  const remainingGroups = groupNames.slice(VISIBLE_GROUP_COUNT);

  if (remainingGroups.length === 0) {
    return <span>{visibleGroups.join(', ')}</span>;
  }

  const remainingData = Object.fromEntries(remainingGroups.map((name, idx) => [`Group ${VISIBLE_GROUP_COUNT + idx + 1}`, name]));

  return (
    <span>
      {visibleGroups.join(', ')}{' '}
      <DynamicDataTooltip data={remainingData}>
        <span className="text-gray-400 cursor-help">+{remainingGroups.length} more</span>
      </DynamicDataTooltip>
    </span>
  );
};

const PolicyTable: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'Group' | 'Automation'>('Automation');
  const [policyData, setPolicyData] = useState<Policy[]>([]);
  const [agentCounts, setAgentCounts] = useState<Record<string, number>>({});
  const [loadingCounts, setLoadingCounts] = useState<Record<string, boolean>>({}); // Track which groups are loading
  const [loading, setLoading] = useState<boolean>(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);
  const { selectedTenantId } = useTenantSelection();

  const navigate = useNavigate();
  const auth = useAuth();

  const { perms, canEdit } = usePermissions();
  const canEditPolicies = canEdit('groupPolicies');
  const canViewPolicies = !!perms.groupPolicies;
  const hasActionColumn = canEditPolicies || canViewPolicies;

  const CreateGroup = () => navigate('/Group');

  const getGroupPolicy = async () => {
    setLoading(true);
    try {
      if (auth && auth.userData) {
        const token = auth.userData.access_token;
        let tenantId = selectedTenantId;
        const response = await api.get(`${Project.api}grouppolicyservice/GroupPolicyAutomation/getgrouppolicyautomation/${tenantId}/`);

        const data = response.data?.data ?? [];
        setPolicyData(data);

        data.forEach((policy: Policy) => {
          policy.tenantGroups?.forEach((group: TenantGroup) => {
            getGroupAgents(policy.tenantId, group.tenantGroupId || group.tenantGroupID || '');
          });
        });
      }
    } catch (error) {
      console.error('Error fetching group policy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteGroupPolicy = async (groupPolicyAutomationId: string) => {
    try {
      const token = auth?.userData?.access_token;
      let tenantId = selectedTenantId;
      await api.delete(`${Project.api}grouppolicyservice/GroupPolicyAutomation/deleteplan/${tenantId}/${groupPolicyAutomationId}`);
      getGroupPolicy(); // Refresh list after delete
    } catch (error) {
      console.error('Error deleting group policy:', error);
      getGroupPolicy();
    }
  };

  const getGroupAgents = async (tenantId: string, tenantGroupId: string) => {
    try {
      if (!tenantGroupId) return;

      // Mark this group as loading
      setLoadingCounts((prev) => ({ ...prev, [tenantGroupId]: true }));

      const token = auth?.userData?.access_token;
      const response = await api.get(`${Project.api}tenantservice/TenantGroup/${tenantId}/agents/${tenantGroupId}`);

      const count = response.data?.data?.[0]?.agents?.length ?? 0;
      setAgentCounts((prev) => ({ ...prev, [tenantGroupId]: count }));

      // Mark this group as done loading
      setLoadingCounts((prev) => ({ ...prev, [tenantGroupId]: false }));
    } catch (error) {
      console.error(`Error fetching agents for group ${tenantGroupId}:`, error);
      // Mark as done even on error
      setLoadingCounts((prev) => ({ ...prev, [tenantGroupId]: false }));
    }
  };

  useEffect(() => {
    getGroupPolicy();
  }, []);

  return (
    <div style={{ padding: '1rem', background: '#111', color: '#fff', fontFamily: 'Arial, sans-serif' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
        {['Group', 'Automation'].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab as 'Group' | 'Automation');
              if (tab === 'Group') CreateGroup();
            }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              borderRight: activeTab === tab ? '5.62px solid #D12929' : '5.62px solid #474646',
              borderLeft: activeTab === tab ? '5.62px solid #D12929' : '5.62px solid #474646',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            {tab}
          </button>
        ))}
        {canEditPolicies && (
          <button
            className={Styles['automationBtn']}
            style={{ marginLeft: 'auto', cursor: 'pointer', whiteSpace: 'nowrap' }}
            onClick={() => navigate('/groupPolicy/CreatePolicy')}
          >
            Create Policy Automation
          </button>
        )}
      </div>

      {/* Table */}
      <div
        style={{
          background: '#1e1e1e',
          borderRadius: '8px',
          overflow: 'hidden',
          marginTop: '20px',
          padding: '20px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 3fr 2fr 1fr',
            marginBottom: '10px',
            whiteSpace: 'nowrap',
          }}
          className={Styles['tableHead']}
        >
          <div style={{ marginLeft: '10px' }}>Policy Name</div>
          <div>Groups Added</div>
          <div>No of Devices Enrolled</div>
          {hasActionColumn && <div>Actions</div>}
        </div>

        {showDeleteModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(18, 15, 15, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <div
              style={{
                background: '#1D1D1D',
                color: '#FEFEFE',
                padding: '2rem',
                borderRadius: '30px',
                width: '90%',
                maxWidth: '500px',
                textAlign: 'center',
              }}
            >
              <img src="/images/delete-icon.svg" alt="delete" style={{ marginLeft: '200px', marginBottom: '1rem' }} />
              <h5>Are you sure you want to delete?</h5>
              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#1D1D1D',
                    border: '1px solid #FEFEFE',
                    borderRadius: '30px',
                    cursor: 'pointer',
                    marginRight: '30px',
                  }}
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedDeleteId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#D12929',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '30px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (selectedDeleteId) {
                      deleteGroupPolicy(selectedDeleteId);
                    }
                    setShowDeleteModal(false);
                    setSelectedDeleteId(null);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loader or Empty */}
        {loading ? (
          <div className="py-12 text-center">
            <LoaderCircle className="w-8 h-8 mx-auto animate-spin text-gray-400" />
          </div>
        ) : policyData.length === 0 ? (
          <div className="py-12 text-center text-gray-400">No records found</div>
        ) : (
          policyData.map((policy, idx) => (
            <div
              key={policy.groupPolicyAutomationId}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 3fr 2fr 1fr',
                padding: '1rem',
                borderBottom: '1px solid #2a2a2a',
                background: idx % 2 === 0 ? '#161617' : '#232222',
                alignItems: 'center',
              }}
            >
              <div
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(`/patchCenter/ActivityPatch?view=table&mode=Automated&groupPolicyAutomationId=${policy.groupPolicyAutomationId}`)
                }
              >
                {policy.policyName}
              </div>
              <div>{renderGroupsCell(policy.tenantGroups)}</div>
              <div style={{ marginLeft: '50px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Check if any group in this policy is loading */}
                {policy.tenantGroups?.some((group) => {
                  const id = group.tenantGroupId || group.tenantGroupID || '';
                  return loadingCounts[id];
                }) ? (
                  <LoaderCircle className="w-5 h-5 animate-spin text-gray-400" />
                ) : (
                  policy.tenantGroups?.reduce((total, group) => {
                    const id = group.tenantGroupId || group.tenantGroupID || '';
                    return total + (agentCounts[id] ?? 0);
                  }, 0)
                )}
              </div>
              {hasActionColumn && (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    title={canEditPolicies ? 'Edit' : 'View'}
                    style={iconButtonStyle}
                    onClick={() => navigate(`/groupPolicy/CreatePolicy/${policy.groupPolicyAutomationId}`)}
                  >
                    {canEditPolicies ? <FaEdit /> : <FaEye />}
                  </button>
                  {/* <button title="Duplicate" style={iconButtonStyle}>
                  <FaCopy />
                </button> */}
                  {canEditPolicies && (
                    <button
                      title="Delete"
                      style={iconButtonStyle}
                      onClick={() => {
                        setSelectedDeleteId(policy.groupPolicyAutomationId);
                        setShowDeleteModal(true);
                      }}
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const iconButtonStyle: React.CSSProperties = {
  background: '#2d2d2d',
  border: 'none',
  padding: '0.4rem',
  borderRadius: '6px',
  color: '#fff',
  cursor: 'pointer',
};

export default PolicyTable;
